/**
 * Short Videos Component
 * G2 digital - https://g2digital.com.br/
 */
class ShortVideosComponent {
  constructor() {
    this.isInitialized = false;
    this.videoThumbnails = [];
    this.previewVideos = [];
    this.overlay = null;
    this.popupVideo = null;
    this.closeButton = null;
    this.loadingSpinner = null;
    this.intersectionObserver = null;
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    this.cacheElements();
    this.setupIntersectionObserver();
    this.bindEvents();
    this.isInitialized = true;

    this.previewVideos.forEach(video => {
      video.controls = false;
      video.muted = true;
    });

    if (this.popupVideo) {
      this.popupVideo.controls = false;
    }
  }

  cacheElements() {
    this.videoThumbnails = document.querySelectorAll('.video-thumbnail-circle');
    this.previewVideos = document.querySelectorAll('.preview-video');
    this.overlay = document.getElementById('video-overlay');
    this.popupVideo = document.getElementById('popup-video');
    this.closeButton = document.querySelector('.close-video');
    this.loadingSpinner = document.querySelector('.loading-spinner');
  }

  setupIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          this.loadAndPlayPreview(video);
        } else {
          this.pausePreview(video);
        }
      });
    }, observerOptions);

    this.previewVideos.forEach(video => {
      this.intersectionObserver.observe(video);
    });
  }

  async loadAndPlayPreview(video) {
    try {
      if (!video.src && video.dataset.previewSrc) {
        video.src = video.dataset.previewSrc;
        await video.load();
      }
      
      if (video.readyState >= 2) {
        await this.playPreview(video);
      } else {
        video.addEventListener('loadeddata', () => this.playPreview(video), { once: true });
      }
    } catch (error) {
      console.warn('Erro ao carregar preview:', error);
      this.showFallbackImage(video);
    }
  }

  async playPreview(video) {
    try {
      video.currentTime = 0;
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        await playPromise;
      }
    } catch (error) {
      console.warn('Erro play no preview:', error);
      this.showFallbackImage(video);
    }
  }

  pausePreview(video) {
    if (!video.paused) {
      video.pause();
    }
  }

  showFallbackImage(video) {
    const container = video.closest('.video-thumbnail-circle');
    const fallbackImage = container?.querySelector('.fallback-image');
    
    if (fallbackImage) {
      video.style.display = 'none';
      fallbackImage.style.display = 'block';
    }
  }

  bindEvents() {
    this.videoThumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => this.handleThumbnailClick(e));
    
      thumbnail.addEventListener('mouseenter', (e) => {
        const video = e.currentTarget.querySelector('.preview-video');
        if (video) this.pausePreview(video);
      });
      
      thumbnail.addEventListener('mouseleave', (e) => {
        const video = e.currentTarget.querySelector('.preview-video');
        if (video && this.isVideoInViewport(video)) {
          this.playPreview(video);
        }
      });
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeModal());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.closeModal();
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay?.classList.contains('active')) {
        this.closeModal();
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllPreviews();
      } else {
        this.resumeVisiblePreviews();
      }
    });
  }

  async handleThumbnailClick(event) {
    const thumbnail = event.currentTarget;
    const videoUrl = thumbnail.getAttribute('data-video-url');
    const mimeType = thumbnail.getAttribute('data-mime-type');
    const audioEnabled = thumbnail.getAttribute('data-audio-enabled') === 'true';

    if (!videoUrl || !this.overlay || !this.popupVideo) return;

    this.overlay.classList.add('active');
    this.loadingSpinner.style.display = 'block';
    document.body.style.overflow = 'hidden';

    this.popupVideo.muted = !audioEnabled;

    try {
      this.popupVideo.src = videoUrl;
      this.popupVideo.type = mimeType;
      
      await this.popupVideo.load();
      
      await new Promise((resolve, reject) => {
        const handleCanPlay = () => {
          this.loadingSpinner.style.display = 'none';
          this.popupVideo.play()
            .then(resolve)
            .catch(reject);
        };
        
        const handleError = () => reject(new Error('Video load error'));
        
        if (this.popupVideo.readyState >= 3) {
          handleCanPlay();
        } else {
          this.popupVideo.addEventListener('canplay', handleCanPlay, { once: true });
          this.popupVideo.addEventListener('error', handleError, { once: true });
        }
      });
      
    } catch (error) {
      console.error('Error loading popup video:', error);
      this.closeModal();
      this.showVideoError();
    }
  }

  closeModal() {
    if (!this.overlay) return;
    
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
    
    if (this.popupVideo) {
      this.popupVideo.pause();
      this.popupVideo.src = '';
      this.popupVideo.load(); 
    }
    
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = 'none';
    }
    
    this.resumeVisiblePreviews();
  }

  showVideoError() {
    console.error('Erro ao carregar o vídeo.');
  }

  isVideoInViewport(video) {
    const rect = video.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  }

  pauseAllPreviews() {
    this.previewVideos.forEach(video => this.pausePreview(video));
  }

  resumeVisiblePreviews() {
    this.previewVideos.forEach(video => {
      if (this.isVideoInViewport(video)) {
        this.playPreview(video);
      }
    });
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.pauseAllPreviews();
    this.isInitialized = false;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-short-videos-component]')) {
    window.shortVideosComponent = new ShortVideosComponent();
  }
});

document.addEventListener('shopify:section:load', () => {
  if (document.querySelector('[data-short-videos-component]')) {
    if (window.shortVideosComponent) {
      window.shortVideosComponent.destroy();
    }
    window.shortVideosComponent = new ShortVideosComponent();
  }
});

document.addEventListener('shopify:section:unload', () => {
  if (window.shortVideosComponent) {
    window.shortVideosComponent.destroy();
    window.shortVideosComponent = null;
  }
});